import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail } from "lucide-react";
import {
  BRAND,
  ADDRESS,
  SOCIAL,
  NAV_LINKS,
  LEGAL_LINKS,
  CONTACT,
} from "@/config/site";
import { BookingButton } from "@/components/common/BookingButton";

export const Footer = () => (
  <footer
    className="relative overflow-hidden bg-[#0f1a18] border-t border-white/10"
    data-testid="site-footer"
  >
    <div className="absolute inset-0 grain" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-10">
      <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr] lg:gap-16">
        <div>
          <div className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Legacy Spa logo"
              className="h-12 w-12 rounded-full bg-white object-contain p-1"
            />
            <span className="font-heading text-2xl text-white">Legacy Spa</span>
          </div>
          <p className="mt-5 font-heading text-2xl md:text-3xl text-legacy-mint italic max-w-md leading-snug">
            {BRAND.motto}
          </p>
          <p className="mt-6 text-sm text-white/55 max-w-sm leading-relaxed">
            Premium massage therapy and wellness serving Greater Houston —
            in-suite, mobile, and corporate wellness. {BRAND.endorsement}.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {BRAND.positioning.map((p) => (
              <span
                key={p}
                className="rounded-full border border-legacy-gold/30 bg-legacy-gold/10 px-3 py-1 text-xs text-legacy-gold"
              >
                {p}
              </span>
            ))}
          </div>
        </div>

        <nav aria-label="Footer navigation">
          <h2 className="text-xs uppercase tracking-[0.2em] text-white/40 font-medium">
            Explore
          </h2>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-sm text-white/70 transition-colors hover:text-legacy-mint"
                  data-testid={`footer-nav-${link.to.replace(/\//g, "") || "home"}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] text-white/40 font-medium">
            Contact
          </h2>
          <address className="not-italic mt-5 flex items-start gap-2 text-sm text-white/70">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-legacy-mint" aria-hidden="true" />
            <span>
              {ADDRESS.line1}, {ADDRESS.line2}
              <br />
              {ADDRESS.city}, {ADDRESS.state} {ADDRESS.zip}
            </span>
          </address>
          <a
            href={CONTACT.phoneHref}
            className="mt-4 flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-legacy-mint"
            data-testid="footer-phone"
          >
            <Phone className="h-4 w-4 text-legacy-mint" aria-hidden="true" />
            {CONTACT.phone}
          </a>
          <a
            href={CONTACT.emailHref}
            className="mt-3 flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-legacy-mint"
            data-testid="footer-email"
          >
            <Mail className="h-4 w-4 text-legacy-mint" aria-hidden="true" />
            {CONTACT.email}
          </a>
          <a
            href={SOCIAL.instagram.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-legacy-mint"
            data-testid="footer-instagram"
          >
            <Instagram className="h-4 w-4" aria-hidden="true" />
            {SOCIAL.instagram.handle}
          </a>
          <div className="mt-6">
            <BookingButton label="Book Now" size="sm" testId="footer-book-btn" />
          </div>
        </div>
      </div>

      <div
        className="mt-16 select-none font-heading text-[16vw] leading-none text-white/[0.04] tracking-tight"
        aria-hidden="true"
      >
        Legacy Spa
      </div>

      <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
        <p className="text-xs text-white/45">
          © {new Date().getFullYear()} {BRAND.legalName}. {BRAND.endorsement}.
        </p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2">
          {LEGAL_LINKS.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className="text-xs text-white/55 transition-colors hover:text-legacy-mint"
                data-testid={`footer-legal-${link.to.replace(/\//g, "")}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </footer>
);
